import {
  exact,
  fallback,
  readString,
  segment,
  type V2ConsoleTarget
} from '@/utils/v2-console-target/shared'

const workspacePaths: Record<string, string> = {
  WorkspaceOverviewPage: '',
  WorkspaceAgentsPage: '/agents',
  WorkspaceFunctionsPage: '/functions',
  WorkspaceConnectorsPage: '/pulsar-io',
  WorkspacePulsarConnectorsPage: '/pulsar-io',
  WorkspaceKafkaConnectorsPage: '/kafka-connect',
  WorkspaceSettingsPage: '/settings/general',
  WorkspaceSettingsGeneralPage: '/settings/general',
  WorkspaceSettingsConnectionsPage: '/settings/connections',
  WorkspaceMcpConfigPage: '/settings/connections',
  WorkspaceFunctionDeployCliPage: '/functions/new',
  WorkspacePulsarConnectorCreatePage: '/pulsar-io/new',
  WorkspaceKafkaConnectorCreatePage: '/kafka-connect/new'
}

type WorkspaceTargetContext = {
  routeName: string
  params?: Record<string, unknown>
  workspaceBase?: string
  workspaceFallback: V2ConsoleTarget
  instanceFallback: V2ConsoleTarget
}

export function resolveWorkspaceTarget({
  routeName,
  params,
  workspaceBase,
  workspaceFallback,
  instanceFallback
}: WorkspaceTargetContext): V2ConsoleTarget | undefined {
  const workspaceSuffix = workspacePaths[routeName]
  if (workspaceSuffix !== undefined) {
    return workspaceBase ? exact(`${workspaceBase}${workspaceSuffix}`) : instanceFallback
  }

  if (routeName === 'WorkspaceAgentDeployCliPage') {
    return workspaceBase ? fallback(`${workspaceBase}/agents`, 'agents') : instanceFallback
  }
  if (routeName === 'WorkspaceAddConnectionPage') {
    return workspaceBase
      ? fallback(`${workspaceBase}/settings/connections`, 'workspaceConnections')
      : instanceFallback
  }

  const name = readString(params, 'name')
  if (
    routeName === 'WorkspaceAgentPage' ||
    routeName === 'WorkspaceAgentOverview' ||
    routeName === 'WorkspaceAgentDashboard'
  ) {
    return workspaceBase && name
      ? exact(`${workspaceBase}/agents/${segment(name)}`)
      : workspaceFallback
  }

  if (
    routeName === 'WorkspaceAgentLogs' ||
    routeName === 'WorkspaceAgentExceptions' ||
    routeName === 'WorkspaceAgentTools'
  ) {
    return workspaceBase && name
      ? fallback(`${workspaceBase}/agents/${segment(name)}`, 'agent')
      : workspaceFallback
  }

  if (
    routeName === 'WorkspaceFunctionPage' ||
    routeName === 'WorkspaceFunctionOverview' ||
    routeName === 'WorkspaceFunctionDashboard'
  ) {
    return workspaceBase && name
      ? exact(`${workspaceBase}/functions/${segment(name)}`)
      : workspaceFallback
  }

  if (routeName === 'WorkspaceFunctionLogs' || routeName === 'WorkspaceFunctionExceptions') {
    return workspaceBase && name
      ? fallback(`${workspaceBase}/functions/${segment(name)}`, 'function')
      : workspaceFallback
  }

  const connectorKind = routeName.includes('Kafka') ? 'kafka-connect' : 'pulsar-io'
  if (
    routeName === 'WorkspaceConnectorPage' ||
    routeName === 'WorkspaceConnectorDashboard' ||
    routeName === 'WorkspaceKafkaConnectorPage' ||
    routeName === 'WorkspaceKafkaConnectorDashboard'
  ) {
    return workspaceBase && name
      ? exact(`${workspaceBase}/${connectorKind}/${segment(name)}`)
      : workspaceFallback
  }

  if (
    routeName === 'WorkspaceConnectorLogs' ||
    routeName === 'WorkspaceConnectorExceptions' ||
    routeName === 'WorkspaceKafkaConnectorLogs' ||
    routeName === 'WorkspaceKafkaConnectorConfigurationPage'
  ) {
    return workspaceBase && name
      ? fallback(`${workspaceBase}/${connectorKind}/${segment(name)}`, 'connector')
      : workspaceFallback
  }

  if (
    routeName === 'WorkspacePulsarConnectorEditPage' ||
    routeName === 'WorkspaceKafkaConnectorEditPage'
  ) {
    return workspaceBase && name
      ? exact(`${workspaceBase}/${connectorKind}/${segment(name)}/edit`)
      : workspaceFallback
  }

  return undefined
}
